import { useEffect } from 'react';
import { useApiData } from '../../../../contexts/apiService';
import { COMPONENT_KEYS, COMPONENT_LABELS, type PricingStrategyConfig, type BswBracket } from '../rfpTypes';
import NumericInput from './NumericInput';

interface PricingStrategyPanelProps {
    strategy: PricingStrategyConfig;
    onChange: (s: PricingStrategyConfig) => void;
    month: number;
    year: number;
}

const MONTH_MAP: Record<string, number> = {
    January: 1, February: 2, March: 3, April: 4,
    May: 5, June: 6, July: 7, August: 8,
    September: 9, October: 10, November: 11, December: 12,
};

export default function PricingStrategyPanel({ strategy, onChange, month, year }: PricingStrategyPanelProps) {
    const { apiData } = useApiData();

    // Re-populate component values whenever the selected month/year changes.
    // Picks the most-recent row at or before the selected period (effective-date lookup).
    useEffect(() => {
        const rows = apiData?.components_pricing;
        if (!rows?.length) return;

        const target = year * 100 + month;
        const toVal = (r: typeof rows[0]) =>
            Number(r.year) * 100 + (MONTH_MAP[String(r.month)] ?? Number(r.month));

        const eligible = rows.filter(r => toVal(r) <= target);
        const row = eligible.length
            ? eligible.reduce((best, r) => toVal(r) > toVal(best) ? r : best)
            : [...rows].reduce((best, r) => toVal(r) > toVal(best) ? r : best); // fallback: nearest available

        const newValues: Record<string, number> = {};
        for (const key of COMPONENT_KEYS) {
            const raw = (row as Record<string, unknown>)[key];
            newValues[key] = raw !== undefined && raw !== null ? Number(raw) : 0;
        }

        onChange({ ...strategy, component_values: newValues });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiData?.components_pricing, month, year]);

    const setFlag = (key: string, checked: boolean) =>
        onChange({ ...strategy, component_flags: { ...strategy.component_flags, [key]: checked } });

    const setValue = (key: string, val: number) =>
        onChange({ ...strategy, component_values: { ...strategy.component_values, [key]: val } });

    const addBswBracket = () => {
        const last = strategy.bsw_schedule.at(-1);
        const newBracket: BswBracket = {
            bsw_low: last ? last.bsw_high : 0,
            bsw_high: last ? last.bsw_high + 1 : 1,
            discount: 0,
            deduct_type: 'fixed',
        };
        onChange({ ...strategy, bsw_schedule: [...strategy.bsw_schedule, newBracket] });
    };

    const updateBracket = (i: number, field: keyof BswBracket, val: string | number) => {
        const updated = strategy.bsw_schedule.map((b, idx) =>
            idx === i ? { ...b, [field]: val } : b
        );
        onChange({ ...strategy, bsw_schedule: updated });
    };

    const removeBracket = (i: number) => {
        onChange({ ...strategy, bsw_schedule: strategy.bsw_schedule.filter((_, idx) => idx !== i) });
    };

    return (
        <div>
            <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700 }}>Purchase Pricing</h4>

            {/* Base Deduct */}
            <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
                <label style={{ fontSize: 13, fontWeight: 600, minWidth: 160 }}>Base Deduct ($/bbl)</label>
                <NumericInput
                    value={strategy.base_deduct}
                    onChange={(v) => onChange({ ...strategy, base_deduct: v })}
                    style={{ padding: '3px 8px', border: '1px solid #d1d5db', borderRadius: 4, width: 110, fontSize: 13 }}
                />
            </div>

            {/* WTI/WTL Gravity Split */}
            <div style={{ marginBottom: 14, padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>
                    <input
                        type="checkbox"
                        checked={strategy.wti_wtl_enabled}
                        onChange={(e) => onChange({ ...strategy, wti_wtl_enabled: e.target.checked })}
                    />
                    WTI / WTL Gravity Split
                </label>
                {strategy.wti_wtl_enabled && (
                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                        <span style={{ color: '#6b7280' }}>≥ API gravity threshold:</span>
                        <input
                            type="number"
                            step="0.1"
                            value={strategy.wti_wtl_gravity_threshold}
                            onChange={(e) =>
                                onChange({ ...strategy, wti_wtl_gravity_threshold: parseFloat(e.target.value) || 44 })
                            }
                            style={{ padding: '2px 6px', border: '1px solid #d1d5db', borderRadius: 4, width: 70 }}
                        />
                        <span style={{ color: '#6b7280' }}>
                            → WTL;&nbsp;below → Mid Cush Diff
                        </span>
                    </div>
                )}
            </div>

            {/* Component Rows */}
            <div style={{ marginBottom: 14 }}>
                <span style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>
                    Components
                </span>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 20px' }}>
                    {COMPONENT_KEYS.map((key) => {
                        const isWtlOrMid = key === 'wtl' || key === 'mid_cush_diff';
                        const controlled = isWtlOrMid && strategy.wti_wtl_enabled;
                        const checked = controlled ? true : !!strategy.component_flags[key];

                        return (
                            <label
                                key={key}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    fontSize: 12,
                                    color: controlled ? '#9ca3af' : '#374151',
                                    padding: '2px 0',
                                }}
                            >
                                <input
                                    type="checkbox"
                                    disabled={controlled}
                                    checked={checked}
                                    onChange={(e) => !controlled && setFlag(key, e.target.checked)}
                                />
                                <span style={{ minWidth: 100 }}>{COMPONENT_LABELS[key]}</span>
                                <NumericInput
                                    value={strategy.component_values[key] ?? 0}
                                    onChange={(v) => setValue(key, v)}
                                    style={{
                                        padding: '2px 4px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: 3,
                                        width: 90,
                                        fontSize: 12,
                                        textAlign: 'right',
                                    }}
                                />
                            </label>
                        );
                    })}
                </div>
            </div>

            {/* BSW Deduct Schedule */}
            <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>BS&W Deduct Schedule</span>
                    <button
                        onClick={addBswBracket}
                        style={{
                            fontSize: 11,
                            padding: '2px 8px',
                            border: '1px solid #3b82f6',
                            borderRadius: 4,
                            background: '#eff6ff',
                            color: '#3b82f6',
                            cursor: 'pointer',
                        }}
                    >
                        + Add Bracket
                    </button>
                </div>

                {strategy.bsw_schedule.length === 0 && (
                    <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>No brackets — no BSW deduct applied.</p>
                )}

                {strategy.bsw_schedule.map((bracket, i) => (
                    <div
                        key={i}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, fontSize: 12 }}
                    >
                        <input
                            type="number"
                            step="0.1"
                            value={bracket.bsw_low}
                            onChange={(e) => updateBracket(i, 'bsw_low', parseFloat(e.target.value) || 0)}
                            style={{ width: 70, padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: 3, textAlign: 'right' }}
                        />
                        <span style={{ color: '#6b7280' }}>–</span>
                        <input
                            type="number"
                            step="0.1"
                            value={bracket.bsw_high}
                            onChange={(e) => updateBracket(i, 'bsw_high', parseFloat(e.target.value) || 0)}
                            style={{ width: 70, padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: 3, textAlign: 'right' }}
                        />
                        <span style={{ color: '#6b7280' }}>% → deduct</span>
                        <select
                            value={bracket.deduct_type ?? 'fixed'}
                            onChange={(e) => updateBracket(i, 'deduct_type', e.target.value)}
                            style={{ padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: 3, fontSize: 12, background: '#fff' }}
                        >
                            <option value="fixed">Fixed $/bbl</option>
                            <option value="percent">% of price</option>
                        </select>
                        <NumericInput
                            value={bracket.discount}
                            onChange={(v) => updateBracket(i, 'discount', v)}
                            style={{ width: 80, padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: 3, textAlign: 'right' as const }}
                        />
                        <span style={{ color: '#6b7280' }}>
                            {(bracket.deduct_type ?? 'fixed') === 'percent' ? '%' : '$/bbl'}
                        </span>
                        <button
                            onClick={() => removeBracket(i)}
                            style={{ fontSize: 11, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        >
                            ✕
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}
